import React, { useContext, useEffect, useState } from 'react';
import PageTitle from '../../../components/page-title/PageTitle';
import { useTenantService } from '../../../hooks/services/useTenantService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import IFormError from '../../../interfaces/IFormError';
import IPage from '../../../interfaces/IPage';
import ITenant from '../../../interfaces/ITenant';
import { Container, Form, LatenessContainer, OptionCard, OptionsGrid } from './TenantForm.styled';
import * as yup from 'yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HelpIcon from '@mui/icons-material/Help';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import TuneIcon from '@mui/icons-material/Tune';
import RuleIcon from '@mui/icons-material/Rule';
import BusinessIcon from '@mui/icons-material/Business';
import { sdsGrayscale } from '../../../theme/sds-tokens';
import FieldCoachmark from '../../../components/field-coachmark/FieldCoachmark';
import { showCidForManagerStates } from './coachmarks/ShowCidForManagerPreview';
import { showCidMessageStates } from './coachmarks/ShowCidMessagePreview';
import { useAccessUserTypeDocumentsStates } from './coachmarks/UseAccessUserTypeDocumentsPreview';
import { useUserAccessScopeStates } from './coachmarks/UseUserAccessScopePreview';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
  Chip,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import ThemeOptionContext from '../../../context/theme-option/ThemeOptionContext';
import IValidationOption from '../../../interfaces/IValidationOption';
import CompaniesList from '../../campanies/companies-list/CompaniesList';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';
import { AuthMethod } from '../../../interfaces/IAuthMethod';
import { FiUser, FiKey } from 'react-icons/fi';

interface TenantProps extends IPage { }

const initialTenant: ITenant = {
  id: '',
  companyName: '',
  companyAddress: '',
  email: '',
  password: '',
  useWhatsApp: false,
  whatsAppInstanceId: '',
  baseURLWhatsAppAPI: '',
  whatsAppToken: '',
  useTimeClocking: false,
  allowsAdjustTimeClock: false,
  whatsappClientToken: '',
  useAcessUserTypeDocuments: false,
  useUserAccessScope: false,
  showCIDForManager: false,
  useMeta: false,
  baseURLMetaApi: '',
  metaAppInstanceId: '',
  metaAppBearerToken: '',
  authMethod: undefined,
  seniorAccessKey: '',
  permissionsInitialized: false,
  seniorSecret: '',
  seniorTenantName: '',
  showCidMessage: false,
  cidMessage: ''
};

interface AuthMethodOption {
  value: AuthMethod;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const OPTIONS: AuthMethodOption[] = [
  {
    value: 'PASSWORD',
    label: 'Usuário e senha',
    description: 'Login tradicional com credenciais pessoais.',
    icon: <FiUser size={18} />,
  },
  {
    value: 'KEY_SECRET',
    label: 'Chave e segredo',
    description: 'Autenticação via key e secret, com api Senior.',
    icon: <FiKey size={18} />,
  },
];

const SHOW_CID_COACHMARK_ID = 'coachmark-show-cid-for-manager';
const SHOW_CID_MESSAGE_COACHMARK_ID = 'coachmark-show-cid-message';
const USE_ACCESS_TYPE_DOCUMENTS_COACHMARK_ID = 'coachmark-use-access-user-type-documents';
const USE_USER_ACCESS_SCOPE_COACHMARK_ID = 'coachmark-use-user-access-scope';

const TenantForm = (props: TenantProps) => {
  window.document.title = props.title;
  const [tenant, setTenant] = useState<ITenant>({ ...initialTenant });
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationOptions, setValidationOptions] = React.useState<IValidationOption[]>([]);
  const [showWhatsappInstanteId, setShowWhatsappInstanceId] = useState(false);
  const [showWhatsappToken, setShowWhatsappToken] = useState(false);
  const [showWhatsappClientToken, setShowWhatsappClientToken] = useState(false);
  const [showSeniorSecret, setShowSeniorSecret] = useState(false);
  const [value, setValue] = React.useState(0);

  const { state: themeState } = useContext(ThemeOptionContext);
  const request = useTenantService();
  const toastr = useToastrVanila();
  const valitation = useValidateSchema();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;

  const authMethod: AuthMethod = tenant.authMethod ?? 'PASSWORD';
  const needsCredentials = authMethod === 'KEY_SECRET';

  const isEdit = hasAccessToAction(
    ["parametros-empresa-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const schema = yup.object({
    email: yup
      .string()
      .email('Esse não parece um válido. Informe um e-mail da plataforma SeniorX pra rotinas de integração.')
      .nullable()
      .test((field, schema) => {
        if (!field && (schema.parent.authMethod ?? 'PASSWORD') === 'PASSWORD') {
          return schema.createError({
            message: 'Campo obrigatório para autenticação por usuário e senha.',
          });
        }
        return true;
      }),
    seniorTenantName: yup.string().nullable().test((field, schema) => {
      if (!field && schema.parent.authMethod === 'KEY_SECRET') {
        return schema.createError({
          message: 'Campo obrigatório. Informe o nome do tenant na Senior X.',
        });
      }
      return true;
    }),
    seniorAccessKey: yup.string().nullable().test((field, schema) => {
      if (!field && schema.parent.authMethod === 'KEY_SECRET') {
        return schema.createError({
          message: 'Campo obrigatório. Informe a access key gerada na Senior X.',
        });
      }
      return true;
    }),
    cidMessage: yup.string().nullable().test((field, schema) => {
      if (!field?.trim() && schema.parent.showCidMessage) {
        return schema.createError({
          message: 'Campo obrigatório. Informe a mensagem que será exibida ao colaborador no campo CID.',
        });
      }
      return true;
    }),
    companyName: yup.string(),
    companyAddress: yup.string(),
    useWhatsApp: yup.boolean(),
    whatsAppInstanceId: yup.string().test((field, schema) => {
      if (!field && schema.parent.useWhatsApp) {
        return schema.createError({
          message: `Campo obrigatório. Informe o ID da instância do whatsapp obtido junto á Senior Norte PR.`,
        });
      }
      return true;
    }),
    whatsAppToken: yup.string().test((field, schema) => {
      if (!field && schema.parent.useWhatsApp) {
        return schema.createError({
          message: `Campo obrigatório. Informe o token do whatsapp obtido junto á Senior Norte PR.`,
        });
      }
      return true;
    }),
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const listValidationOptions = async (): Promise<void> => {
    setLoading(true);
    await request
      .listValidationOptions()
      .then(result => {
        setValidationOptions(result);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const getTenant = async () => {
    setLoading(true);
    await request
      .getTenant()
      .then(result => {
        setTenant({ ...result });
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const saveTenant = async () => {
    setLoading(true);
    await request
      .updateTenant(tenant.id, {
        id: tenant.id,
        email: tenant.email,
        password: tenant.password,
        companyName: tenant.companyName,
        companyAddress: tenant.companyAddress,
        useWhatsApp: tenant.useWhatsApp,
        whatsAppInstanceId: tenant.whatsAppInstanceId,
        baseURLWhatsAppAPI: tenant.baseURLWhatsAppAPI,
        whatsAppToken: tenant.whatsAppToken,
        useTimeClocking: tenant.useTimeClocking,
        validationOptionId: tenant.validationOptionId,
        hoursOfLateness: tenant.hoursOfLateness,
        validateLatenessFromBackoffice: tenant.validateLatenessFromBackoffice,
        useUserAccessScope: tenant.useUserAccessScope,
        whatsappClientToken: tenant.whatsappClientToken,
        formatForRecordingNumberOfHoursId: tenant.formatForRecordingNumberOfHoursId,
        allowsAdjustTimeClock: tenant.allowsAdjustTimeClock,
        validationOptionAllowYear: tenant.validationOptionAllowYear,
        documentTypeDisplayMode: tenant.documentTypeDisplayMode,
        useAcessUserTypeDocuments: tenant.useAcessUserTypeDocuments,
        showCIDForManager: tenant.showCIDForManager,
        useMeta: tenant.useMeta,
        baseURLMetaApi: tenant.baseURLMetaApi,
        metaAppInstanceId: tenant.metaAppInstanceId,
        metaAppBearerToken: tenant.metaAppBearerToken,
        permissionsInitialized: tenant.permissionsInitialized,
        authMethod: tenant.authMethod,
        seniorAccessKey: tenant.seniorAccessKey ?? '',
        seniorTenantName: tenant.seniorTenantName ?? '',
        seniorSecret: tenant.seniorSecret || undefined,
        showCidMessage: tenant.showCidMessage,
        cidMessage: tenant.cidMessage
      })
      .then(async result => {
        await getTenant();
        setLoading(false);
        toastr.success('Dados atualizados com sucesso');
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    listValidationOptions();
    getTenant();
  }, []);

  const handleCancel = async () => {
    setErrors(null);
    await getTenant();
    setShowWhatsappInstanceId(false);
    setShowWhatsappToken(false);
    setShowSeniorSecret(false);
  };

  const handleSaveClick = async () => {
    const result = await valitation.validate(schema, tenant);
    setErrors({ ...result.errors });
    if (!result.isValid) {
      toastr.error('Verifique os campos inválidos');
      return;
    } else {
      await saveTenant();
    }
  };

  const handleSelect = (value: AuthMethod) => {
    if (!isEdit) return;
    setTenant({ ...tenant, authMethod: value });
    setErrors(null);
  };

  return (
    <Container isDark={themeState.isDark}>
      <PageTitle
        title="MANUTENÇÃO DE PARÂMETROS DA EMPRESA"
        icon={<SettingsApplicationsIcon sx={{ color: 'primary.main' }} />}
        subtitle={
          <Typography
            variant="helper"
            sx={{ display: 'block', color: themeState.isDark ? sdsGrayscale[40] : sdsGrayscale[60] }}
          >
            Configurações aplicadas as empresas, colaboradores e documentos.
          </Typography>
        }
        footer={
          <>
            <Divider sx={{ marginTop: 2, marginX: '-10px' }} />
            <Tabs
              value={value}
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#1a817a' },
                '& .MuiTab-root.Mui-selected': { color: '#1a817a' },
              }}
            >
              <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Gerais" />
              <Tab icon={<RuleIcon fontSize="small" />} iconPosition="start" label="Validações" />
              <Tab icon={<BusinessIcon fontSize="small" />} iconPosition="start" label="Empresas" />
            </Tabs>
          </>
        }
      />
      {loading && <LinearProgress />}
      <Paper
        elevation={value === 2 ? 0 : 1}
        sx={{
          marginTop: 1,
          bgcolor: value === 2 ? 'transparent' : undefined,
          paddingX: value === 2 ? 0 : 3,
          paddingTop: value === 2 ? 0 : 1,
          paddingBottom: value === 2 ? 0 : 3,
        }}
      >
        <Form>
          {value === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="RAZÃO SOCIAL OU FANTASIA"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Informe a razão social ou nome fantasia. Será utilizado em e-mails e mensagens"
                  value={tenant.companyName || ''}
                  onChange={e => setTenant({ ...tenant, companyName: e.target.value })}
                  error={Boolean(errors?.companyName)}
                  helperText={errors?.companyName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endereço"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Informe o endereço. Será utilizado em e-mails e mensagens"
                  value={tenant.companyAddress || ''}
                  onChange={e => setTenant({ ...tenant, companyAddress: e.target.value })}
                  error={Boolean(errors?.companyAddress)}
                  helperText={errors?.companyAddress}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <VpnKeyIcon fontSize="small" color='info'/>
                    <Typography sx={{ fontWeight: 500 }}>Método de autenticação</Typography>
                  </Box>

<OptionsGrid role="radiogroup">
                    {OPTIONS.map(opt => {
                      const isSelected = authMethod === opt.value;
                      return (
                        <OptionCard
                          key={opt.value}
                          selected={isSelected}
                          onClick={() => handleSelect(opt.value)}
                          role="radio"
                          aria-checked={isSelected}
                          tabIndex={isEdit ? 0 : -1}
                          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSelect(opt.value);
                            }
                          }}
                          sx={!isEdit ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                              justifyContent: 'space-between',
                              color: isSelected ? '#208f76' : 'text.secondary',
                            }}
                          >
                            {opt.icon}
                            {isSelected && (
                              <Chip
                                label="Padrão atual"
                                size="small"
                                sx={{ backgroundColor: '#208f76', color: '#fff', height: 22, fontSize: 11 }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight={500}>
                            {opt.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {opt.description}
                          </Typography>
                        </OptionCard>
                      );
                    })}
                  </OptionsGrid>

                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {needsCredentials ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex' }}>
                          <strong style={{ color: '#208f76' }}> Acesso na Senior X.</strong>
                          <Tooltip
                            arrow
                            placement="right"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  boxShadow: 3,
                                  maxWidth: 420,
                                  p: 1.5,
                                },
                              },
                              arrow: {
                                sx: {
                                  color: 'background.paper',
                                  '&::before': { border: '1px solid', borderColor: 'divider' },
                                },
                              },
                            }}
                            title={
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'inherit' }}>
                                  Como usar
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                  <LooksOneIcon fontSize="small" sx={{ color: 'error.main', mt: '1px' }} />
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'inherit' }}>
                                    Na Senior X, acesse <strong>Tecnologia &gt; Administração &gt; Gestão de aplicações</strong> e
                                    abra as ações da aplicação cadastrada para o Atestado Digital.
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                  <LooksTwoIcon fontSize="small" sx={{ color: 'error.main', mt: '1px' }} />
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'inherit' }}>
                                    Copie a <strong>access key</strong> e o <strong>secret</strong> para os campos abaixo.
                                    O secret só é exibido no momento da geração.
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          >
                            <HelpIcon
                              fontSize="small"
                              sx={{ color: '#208f76', ml: 0.5, cursor: 'help', verticalAlign: 'middle' }}
                            />
                          </Tooltip>
                        </Typography>

                        <TextField
                          fullWidth
                          label="Access key"
                          InputLabelProps={{ shrink: true }}
                          placeholder="Informe a access key da aplicação"
                          value={tenant.seniorAccessKey || ''}
                          onChange={e => setTenant({ ...tenant, seniorAccessKey: e.target.value })}
                          error={Boolean(errors?.seniorAccessKey)}
                          helperText={errors?.seniorAccessKey}
                        />
                        <TextField
                          fullWidth
                          label="Secret"
                          type={showSeniorSecret ? 'text' : 'password'}
                          placeholder="Deixe em branco para manter o segredo já configurado"
                          InputLabelProps={{ shrink: true }}
                          value={tenant.seniorSecret || ''}
                          onChange={e => setTenant({ ...tenant, seniorSecret: e.target.value })}
                          error={Boolean(errors?.seniorSecret)}
                          helperText={errors?.seniorSecret ?? 'Deixe em branco para manter o segredo já configurado.'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton edge="end" onClick={() => setShowSeniorSecret(!showSeniorSecret)}>
                                  {!showSeniorSecret ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          label="E-mail do usuário da Plataforma SeniorX"
                          InputLabelProps={{ shrink: true }}
                          placeholder="Informe um e-mail da plataforma SeniorX pra rotinas de integração"
                          value={tenant.email || ''}
                          onChange={e => setTenant({ ...tenant, email: e.target.value })}
                          error={Boolean(errors?.email)}
                          helperText={errors?.email}
                        />
                        <TextField
                          fullWidth
                          label="Password do usuário admin"
                          InputLabelProps={{ shrink: true }}
                          placeholder="Informe a senha do usuário SeniorX pra rotinas de integração"
                          type="password"
                          value={tenant.password || ''}
                          onChange={e => setTenant({ ...tenant, password: e.target.value })}
                          error={Boolean(errors?.password)}
                          helperText={errors?.password}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="formatfor-recording-number-of-hours" shrink>
                    Formato de lançamento de atestado em horas
                  </InputLabel>
                  <Select
                    labelId="formatfor-recording-number-of-hours"
                    id="formatfor-recording-number-of-hours-select"
                    value={Number(tenant.formatForRecordingNumberOfHoursId)}
                    label="Formato de lançamento de atestado em horas"
                    notched
                    onChange={e => {
                      setTenant({
                        ...tenant,
                        formatForRecordingNumberOfHoursId: Number(e.target.value),
                      });
                    }}
                  >
                    <MenuItem value={1}>Hora inicial e Hora final</MenuItem>
                    <MenuItem value={2}>Hora inicial e Total de Horas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="formatfor-recording-number-of-hours" shrink>
                    Modelo de exibição dos tipos de documentos
                  </InputLabel>
                  <Select
                    labelId="Document-Type-Display-Mode"
                    id="Document-Type-Display-Mode"
                    value={Number(tenant.documentTypeDisplayMode)}
                    label="Modelo de exibição dos tipos de documentos"
                    notched
                    onChange={e => {
                      setTenant({
                        ...tenant,
                        documentTypeDisplayMode: Number(e.target.value),
                      });
                    }}
                  >
                    <MenuItem value={1}>Botão de opção</MenuItem>
                    <MenuItem value={2}>Seletor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      label="Utiliza abrangência de usuários"
                      sx={{ marginRight: 0 }}
                      control={
                        <Checkbox
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              useUserAccessScope: e.target.checked,
                            })
                          }
                          checked={tenant.useUserAccessScope}
                          inputProps={{ 'aria-describedby': USE_USER_ACCESS_SCOPE_COACHMARK_ID }}
                        />
                      }
                    />
                    <FieldCoachmark
                      descriptionId={USE_USER_ACCESS_SCOPE_COACHMARK_ID}
                      title="Followup, relatórios, colaboradores e home"
                      description={
                        'Marcado: quem tiver abrangência cadastrada em Manutenção de usuários › Definir acessos passa a ver só a empresa e a filial do próprio escopo nessas quatro telas.\n' +
                        'Desmarcado: o escopo é ignorado.\n' +
                        'Sem escopo cadastrado, nada é restringido.'
                      }
                      states={useUserAccessScopeStates}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      label="Utiliza abrangência de acesso a documentos por tipo"
                      sx={{ marginRight: 0 }}
                      control={
                        <Checkbox
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              useAcessUserTypeDocuments: e.target.checked,
                            })
                          }
                          checked={tenant.useAcessUserTypeDocuments}
                          inputProps={{ 'aria-describedby': USE_ACCESS_TYPE_DOCUMENTS_COACHMARK_ID }}
                        />
                      }
                    />
                    <FieldCoachmark
                      descriptionId={USE_ACCESS_TYPE_DOCUMENTS_COACHMARK_ID}
                      title="Manutenção de usuários › Ações da listagem"
                      description={
                        "Marcado: a ação 'Definir tipos de documentos' fica disponível, que abre a janela de permissões para escolher quais tipos de documento aquele usuário tem acesso.\n" +
                        'Desmarcado: a ação não aparece e não há restrição por tipo.'
                      }
                      states={useAccessUserTypeDocumentsStates}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      label="Exibir CID para gestores"
                      sx={{ marginRight: 0 }}
                      control={
                        <Checkbox
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              showCIDForManager: e.target.checked,
                            })
                          }
                          checked={tenant.showCIDForManager}
                          inputProps={{ 'aria-describedby': SHOW_CID_COACHMARK_ID }}
                        />
                      }
                    />
                    <FieldCoachmark
                      descriptionId={SHOW_CID_COACHMARK_ID}
                      title="Followup › Ações > Detalhes"
                      description={
                        'Marcado: o gestor vê a CID do atestado e abre os anexos normalmente.\n' +
                        'Desmarcado: a CID aparece como *** e o anexo fica borrado.'
                      }
                      states={showCidForManagerStates}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      label="Exibir mensagem na CID para usuários"
                      sx={{ marginRight: 0 }}
                      control={
                        <Checkbox
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              showCidMessage: e.target.checked,
                            })
                          }
                          checked={tenant.showCidMessage}
                          inputProps={{ 'aria-describedby': SHOW_CID_MESSAGE_COACHMARK_ID }}
                        />
                      }
                    />
                    <FieldCoachmark
                      descriptionId={SHOW_CID_MESSAGE_COACHMARK_ID}
                      title="Enviar documento › informe a CID do atestado"
                      description={
                        'Marcado: o texto configurado abaixo aparece como aviso acima do campo CID, no envio do atestado pelo funcionário.\n' +
                        'Desmarcado: o passo não exibe aviso.'
                      }
                      states={showCidMessageStates}
                    />
                  </Box>
                </Box>
              </Grid>
              {tenant.showCidMessage && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Mensagem exibida no campo CID"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Informe a mensagem que o colaborador verá ao preencher a CID do atestado"
                    value={tenant.cidMessage || ''}
                    onChange={e => setTenant({ ...tenant, cidMessage: e.target.value })}
                    error={Boolean(errors?.cidMessage)}
                    helperText={
                      errors?.cidMessage ?? `${(tenant.cidMessage || '').length} caracteres`
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Accordion
                  sx={theme =>
                    theme.palette.mode === 'dark'
                      ? {
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:before': { display: 'none' },
                        }
                      : {}
                  }
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <WhatsAppIcon sx={{ color: '#1bab74', marginRight: 1.5, marginTop: '2px' }} />
                    <Box>
                      <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                        WhatsApp
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Configurações de acesso
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sx={{ m: 2 }}>
                        <FormControlLabel
                          label="Utilizar PlugZapp"
                          componentsProps={{
                            typography: {
                              sx: { fontWeight: tenant.useWhatsApp ? 700 : 400 },
                            },
                          }}
                          control={
                            <Checkbox
                              onChange={e =>
                                setTenant({
                                  ...tenant,
                                  useWhatsApp: e.target.checked,
                                  useMeta: false,
                                })
                              }
                              color='success'
                              checked={tenant.useWhatsApp}
                            />
                          }
                        />
                        <FormControlLabel
                          label="Utilizar Meta"
                          componentsProps={{
                            typography: {
                              sx: { fontWeight: tenant.useMeta ? 700 : 400 },
                            },
                          }}
                          control={
                            <Checkbox
                              onChange={e =>
                                setTenant({
                                  ...tenant,
                                  useMeta: e.target.checked,
                                  useWhatsApp: false,
                                })
                              }
                              color='success'
                              checked={tenant.useMeta}
                            />
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={tenant.useMeta ? 'Meta URL base' : 'WhatsApp URL base'}
                          placeholder="Informe a URL base da API de envio de mensagem"
                          value={
                            tenant && tenant.useMeta
                              ? (tenant.baseURLMetaApi ?? '')
                              : (tenant.baseURLWhatsAppAPI ?? '')
                          }
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              ...(tenant.useMeta
                                ? { baseURLMetaApi: e.target.value }
                                : { baseURLWhatsAppAPI: e.target.value }),
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(errors?.baseURLWhatsAppAPI)}
                          helperText={errors?.baseURLWhatsAppAPI}
                          disabled={!tenant.useWhatsApp && !tenant.useMeta}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={tenant.useMeta ? 'Meta App Instance ID' : 'WhatsApp InstanceID'}
                          placeholder={
                            tenant.useMeta
                              ? 'Informe o ID da instância Meta'
                              : 'Informe o ID da instância do WhatsApp obtido junto a Senior Norte PR'
                          }
                          value={
                            tenant.useMeta
                              ? (tenant.metaAppInstanceId ?? '')
                              : (tenant.whatsAppInstanceId ?? '')
                          }
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              ...(tenant.useMeta
                                ? { metaAppInstanceId: e.target.value }
                                : { whatsAppInstanceId: e.target.value }),
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(errors?.whatsAppInstanceId)}
                          helperText={errors?.whatsAppInstanceId}
                          type={showWhatsappInstanteId ? 'text' : 'password'}
                          disabled={!tenant.useWhatsApp && !tenant.useMeta}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  edge="end"
                                  onClick={() => setShowWhatsappInstanceId(!showWhatsappInstanteId)}
                                >
                                  {!showWhatsappInstanteId ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={tenant.useMeta ? 'Meta Bearer Token' : 'WhatsApp Token'}
                          placeholder={
                            tenant.useMeta
                              ? 'Informe o Bearer Token da Meta'
                              : 'Informe o token do WhatsApp obtido junto a Senior Norte PR'
                          }
                          value={
                            tenant.useMeta
                              ? (tenant.metaAppBearerToken ?? '')
                              : (tenant.whatsAppToken ?? '')
                          }
                          onChange={e =>
                            setTenant({
                              ...tenant,
                              ...(tenant.useMeta
                                ? { metaAppBearerToken: e.target.value }
                                : { whatsAppToken: e.target.value }),
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(errors?.whatsAppToken)}
                          helperText={errors?.whatsAppToken}
                          type={showWhatsappToken ? 'text' : 'password'}
                          disabled={!tenant.useWhatsApp && !tenant.useMeta}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  edge="end"
                                  onClick={() => setShowWhatsappToken(!showWhatsappToken)}
                                >
                                  {!showWhatsappToken ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {tenant.useWhatsApp && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="WhatsApp Client-Token"
                            placeholder="Informe o Client-Token do WhatsApp obtido junto a Senior Norte PR"
                            value={tenant.whatsappClientToken ?? ''}
                            onChange={e => setTenant({ ...tenant, whatsappClientToken: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(errors?.whatsappClientToken)}
                            helperText={errors?.whatsappClientToken}
                            type={showWhatsappClientToken ? 'text' : 'password'}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    edge="end"
                                    onClick={() => setShowWhatsappClientToken(!showWhatsappClientToken)}
                                  >
                                    {!showWhatsappClientToken ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid item xs={12}>
                <Accordion
                  sx={theme =>
                    theme.palette.mode === 'dark'
                      ? {
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:before': { display: 'none' },
                        }
                      : {}
                  }
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <PointOfSaleIcon sx={{ color: '#1975d1', marginRight: 1.5 }}></PointOfSaleIcon>
                    <Box>
                      <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                        Controle de Ponto
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Configurações de acesso
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label="Utiliza controle de ponto"
                          control={
                            <Checkbox
                              onChange={e =>
                                setTenant({
                                  ...tenant,
                                  useTimeClocking: e.target.checked,
                                })
                              }
                              checked={tenant.useTimeClocking}
                            />
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          label="Permite ajuste de ponto"
                          control={
                            <Checkbox
                              onChange={e =>
                                setTenant({
                                  ...tenant,
                                  allowsAdjustTimeClock: e.target.checked,
                                })
                              }
                              checked={tenant.allowsAdjustTimeClock}
                            />
                          }
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          )}

          {value === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <LatenessContainer>
                  <LatenessContainer style={{ minWidth: '20px', marginRight: '10px' }}>
                    <span>Atestados enviados após</span>
                  </LatenessContainer>

                  <LatenessContainer>
                    <TextField
                      value={tenant.hoursOfLateness || ''}
                      onChange={e => setTenant({ ...tenant, hoursOfLateness: parseInt(e.target.value) })}
                      type="number"
                      variant="standard"
                      sx={{ width: '50px', marginRight: '10px' }}
                    />
                    <span style={{ marginRight: '10px' }}>Horas</span>
                  </LatenessContainer>

                  <FormControl variant="standard" sx={{ minWidth: '18px' }}>
                    <Select
                      value={tenant.validationOptionId || ''}
                      onChange={e => setTenant({ ...tenant, validationOptionId: e.target.value as number })}
                    >
                      {validationOptions.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </LatenessContainer>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="formatfor-recording-number-of-hours">
                    Quando exceder quantidade de documentos permitidos por ano
                  </InputLabel>
                  <Select
                    labelId="formatfor-recording-number-of-hours"
                    id="formatfor-recording-number-of-hours-select"
                    value={tenant.validationOptionAllowYear}
                    label="Quando exceder quantidade de documentos permitidos por ano"
                    onChange={e => {
                      setTenant({
                        ...tenant,
                        validationOptionAllowYear: Number(e.target.value),
                      });
                    }}
                  >
                    <MenuItem value={1}>1 - Criar Alerta</MenuItem>
                    <MenuItem value={2}>2 - Reprovar Automáticamente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label="Validar também quando enviado pelo RH"
                  control={
                    <Checkbox
                      onChange={e => setTenant({ ...tenant, validateLatenessFromBackoffice: e.target.checked })}
                      checked={tenant.validateLatenessFromBackoffice}
                    />
                  }
                />
              </Grid>
            </Grid>
          )}
          {value === 2 && (
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <CompaniesList title="Atestado | Empresa" />
              </Grid>
            </Grid>
          )}
        </Form>
      </Paper>
      {value !== 2 && (
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ marginTop: 5 }}
          spacing={2}
        >
          <Grid item>
            <LoadingButton loading={loading} variant="contained" color="secondary" onClick={handleCancel}>
              Cancelar
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSaveClick} disabled={!isEdit}>
              Salvar
            </LoadingButton>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default TenantForm;
